import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { ROOT_URL } from 'utils';

export type BetMode = 'arcade' | 'real';

type ModeContextValue = {
  mode: BetMode;
  setMode: (m: BetMode) => void;
  toggleMode: () => void;
  realEnabled: boolean;
  onboardingEnabled?: boolean;
  withdrawEnabled?: boolean;
  requireKyc?: boolean;
  pricingVersion?: string;
  faucetEnabled?: boolean;
  risk?: {
    margins?: { baseMargin: number; drawExtraMargin: number; extraMarginLowConf?: number };
    confidence?: { earlyMoveNum?: number };
    maxOdds?: { white_win: number; draw: number; black_win: number };
  };
  limits?: { arcadeMaxStakeMove: number; arcadeMaxStakeWdl: number; arcadeMoveMargin: number; poolRake: number };
};

const ModeContext = createContext<ModeContextValue | undefined>(undefined);

const STORAGE_KEY = 'betmate.mode';

function getInitialMode(): BetMode {
  if (typeof window === 'undefined') return 'arcade';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return (stored === 'real' || stored === 'arcade') ? (stored as BetMode) : 'arcade';
}

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<BetMode>(getInitialMode);
  const [realEnabled, setRealEnabled] = useState<boolean>(true);
  // Default: suppress onboarding in local/dev for friction-free flows
  const defaultOnboarding = !((process.env.TARGET_ENV === 'local' || process.env.NODE_ENV !== 'production'));
  const [onboardingEnabled, setOnboardingEnabled] = useState<boolean | undefined>(defaultOnboarding);
  const [withdrawEnabled, setWithdrawEnabled] = useState<boolean | undefined>(undefined);
  const [requireKyc, setRequireKyc] = useState<boolean | undefined>(undefined);
  const [pricingVersion, setPricingVersion] = useState<string | undefined>(undefined);
  const [faucetEnabled, setFaucetEnabled] = useState<boolean | undefined>(undefined);
  const [risk, setRisk] = useState<ModeContextValue['risk']>(undefined);
  const [limits, setLimits] = useState<ModeContextValue['limits']>(undefined);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
  }, [mode]);

  // Reflect current betting mode on the <html> element for CSS hooks
  useEffect(() => {
    try {
      const html = document.documentElement;
      if (!html) return;
      if (mode === 'real') {
        html.classList.add('mode-real');
        html.classList.remove('mode-arcade');
      } else {
        html.classList.add('mode-arcade');
        html.classList.remove('mode-real');
      }
    } catch {}
  }, [mode]);

  // Optional sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'arcade' || e.newValue === 'real')) {
        setModeState(e.newValue as BetMode);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fetch feature flags from backend
  useEffect(() => {
    let isMounted = true;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${ROOT_URL}/api/status`);
        const json = await res.json();
        const enabled = !!json?.features?.realModeEnabled;
        const pv: string | undefined = json?.pricing?.pricingModelVersion || undefined;
        const fe: boolean | undefined = json?.features?.enableFaucet;
        const we: boolean | undefined = json?.features?.enableWithdrawals;
        const rkf: boolean | undefined = json?.features?.requireKyc;
        const obe: boolean | undefined = json?.features?.onboardingEnabled;
        const rk: any = json?.risk || undefined;
        const lm: any = json?.limits || undefined;
        if (!isMounted) return;
        setRealEnabled(enabled);
        setPricingVersion(pv);
        setFaucetEnabled(fe);
        setWithdrawEnabled(we);
        setRequireKyc(rkf);
        // Local/dev override: keep onboarding disabled regardless of server flags
        if (defaultOnboarding === false) {
          setOnboardingEnabled(false);
        } else {
          setOnboardingEnabled(typeof obe === 'boolean' ? obe : true);
        }
        setRisk(rk);
        setLimits(lm);
        if (!enabled && mode === 'real') setModeState('arcade');
      } catch {
        // If status fails, keep previous values
      }
    };
    fetchStatus();
    const onRefresh = () => { fetchStatus(); };
    window.addEventListener('betmate:refresh-status', onRefresh as any);
    return () => { isMounted = false; window.removeEventListener('betmate:refresh-status', onRefresh as any); };
  }, [mode]);

  const setMode = (m: BetMode) => setModeState(m === 'real' && !realEnabled ? 'arcade' : m);
  const toggleMode = () => setModeState((prev) => {
    if (!realEnabled) return 'arcade';
    return prev === 'arcade' ? 'real' : 'arcade';
  });

  const value = useMemo(() => ({
    mode, setMode, toggleMode, realEnabled, onboardingEnabled, withdrawEnabled, requireKyc, pricingVersion, faucetEnabled, risk, limits,
  }), [mode, realEnabled, onboardingEnabled, withdrawEnabled, requireKyc, pricingVersion, faucetEnabled, risk, limits]);
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export function useMode(): ModeContextValue {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error('useMode must be used within ModeProvider');
  return ctx;
}
